import moment from 'moment';
import { useState } from 'react';

import { BannerPlacement, BannerScope, BannerStyle } from '../../../../../typings/enums';
import { parseContentsJSON } from '../../../generic/QuillEditor';
import type { Banner, CreateBannerRequest, UpdateBannerRequest } from '../../../../../shared/types/routes/banners';
import type { TextInputOnChange } from '../../../generic/Forms/InputBase';
import type { QuillInsertOp } from '../../../generic/QuillEditor';

type WithEditorBody<T extends { body: unknown }> = Omit<T, 'body'> & {
  body: {
    contents: QuillInsertOp[];
    length: number;
  };
};

export type NewBanner = WithEditorBody<CreateBannerRequest>;

const hasBody = (ops: QuillInsertOp[]): boolean =>
  ops.some(op => op.insert && op.insert !== '\n');

// When no body was provided, we still have one op that adds a newline because that's how  Quill works, but that creates
// a paragraph. Treat such a body as actually empty.
export const bodyContentsJSON = (banner: NewBanner) =>
  hasBody(banner.body.contents) ? JSON.stringify(banner.body.contents) : '[]';

/**
 * Create a NewBanner object with default values.
 */
export function newBanner(banner: Partial<NewBanner> = {}): NewBanner {
  return {
    title: '',
    body: {
      contents: [],
      length: 0,
    },
    isDismissible: true,
    style: BannerStyle.Low,
    startDate: moment().startOf('day').toISOString(),
    endDate: null,
    scope: { type: BannerScope.Global },
    placement: BannerPlacement.All,
    ...banner,
  };
}

// ScopedOrganizationDisplay is the org metadata (id + title) the banner picker uses to render
// chips with real titles before the user runs any organization search.
type ScopedOrganizationDisplay = { id: number; title: string };

export type AddModeState = {
  mode: 'add';
  banner: WithEditorBody<CreateBannerRequest>;
  // Display-only org metadata, populated when duplicating from an org-scoped banner.
  scopedOrganizations?: ScopedOrganizationDisplay[];
};

export type EditModeState = {
  mode: 'edit';
  // The banner object's "body" property is set to an object that holds Delta insert ops and a length. When the rich
  // text editor for the body is initially mounted, this objects contents are used as the editor's initial contents.
  banner: WithEditorBody<UpdateBannerRequest> & Pick<Banner, 'id' | 'isDeactivated'>,
  // In edit mode, the banner's original title is saved so that it's displayed in the modal even as the user changes the
  // title, that is until the banner is saved.
  originalTitle: string;
  // Display-only org metadata, populated for org-scoped banners on edit.
  scopedOrganizations?: ScopedOrganizationDisplay[];
};

export type State =
  | AddModeState
  | EditModeState;

export type UseAddEditBanner = {
  state: State | null;
  isEdited: boolean;
  onAddNewBanner: () => void;
  onEditBanner: (banner: Banner) => void;
  onDuplicateBanner: (banner: Banner) => void;
  onCloseModal: () => void;
  onTitleChange: TextInputOnChange;
  onBodyChange: (editorState: { contents: QuillInsertOp[]; length: number }) => void;
  onStartDateChange: (startDate: string) => void;
  onEndDateChange: (endDate: string | null) => void;
  onToggleIsDismissible: (isDismissible: boolean) => void;
  onBannerStyleChange: (bannerStyle: BannerStyle) => void;
  onPlacementChange: (placement: BannerPlacement) => void;
  onScopeChange: (scope: BannerScope) => void;
  onScopedOrganizationsChange: (organizationIds: number[]) => void;
};

// Spread of partial banner updates into State widens away the AddModeState | EditModeState
// discriminant; the cast restores it. Centralised here so callers don't repeat it.
const mergeBanner = (prevState: State, updates: Partial<NewBanner>): State =>
  ({ ...prevState, banner: { ...prevState.banner, ...updates } }) as State;

/**
 * Manages state for the add/edit banner modal.
 */
const useAddEditBanner = (): UseAddEditBanner => {
  const [state, setState] = useState<State | null>(null);
  const [isEdited, setIsEdited] = useState<boolean>(false);

  const updateBanner = (updates: Partial<NewBanner>) => {
    setState(prevState => (prevState ? mergeBanner(prevState, updates) : null));
    setIsEdited(true);
  };

  const setBanner = <Field extends keyof NewBanner>(field: Field) => (value: NewBanner[Field]) => {
    updateBanner({ [field]: value });
  };

  const onAddNewBanner = () =>
    setState({
      mode: 'add',
      banner: newBanner(),
    });

  // Build the discriminated scope+placement pair from a persisted Banner. Org-scoped banners
  // are normalized to dashboard placement (login is anonymous and cannot resolve a viewer org).
  const toWriteScopeAndPlacement = (banner: Banner) =>
    banner.scope.type === BannerScope.Organizations
      ? {
          scope: { type: BannerScope.Organizations as const, organizationIds: [...banner.scope.organizationIds] },
          placement: BannerPlacement.Dashboard as const,
        }
      : {
          scope: { type: BannerScope.Global as const },
          placement: banner.placement,
        };

  const onDuplicateBanner = (banner: Banner) => {
    setState({
      mode: 'add',
      banner: newBanner({
        title: `${banner.title} (copy)`,
        body: {
          contents: parseContentsJSON(banner.body),
          // We can use 0 as the default length because it will get updated as soon as the editor's contents are set.
          length: 0,
        },
        isDismissible: banner.isDismissible,
        style: banner.style,
        startDate: banner.startDate,
        endDate: banner.endDate,
        ...toWriteScopeAndPlacement(banner),
      }),
      // Display-only org metadata so the picker shows real titles immediately.
      scopedOrganizations: banner.scopedOrganizations,
    });
    setIsEdited(true);
  };

  const onEditBanner = (banner: Banner) => {
    setState({
      mode: 'edit',
      banner: {
        id: banner.id,
        isDeactivated: banner.isDeactivated,
        title: banner.title,
        body: {
          contents: parseContentsJSON(banner.body),
          // We can use 0 as the default length because it will get updated as soon as the editor's contents are set.
          length: 0,
        },
        isDismissible: banner.isDismissible,
        style: banner.style,
        startDate: banner.startDate,
        endDate: banner.endDate,
        ...toWriteScopeAndPlacement(banner),
      },
      originalTitle: banner.title,
      scopedOrganizations: banner.scopedOrganizations,
    });
  };

  const onCloseModal = () => {
    setState(null);
    setIsEdited(false);
  };

  const onTitleChange = setBanner('title');
  const onBodyChange = setBanner('body');
  const onStartDateChange = setBanner('startDate');
  const onEndDateChange = setBanner('endDate');
  const onToggleIsDismissible = setBanner('isDismissible');
  const onBannerStyleChange = setBanner('style');

  // Placement drives scope: switching to a non-dashboard placement also resets scope to
  // global, since org-scoped banners are dashboard-only (login is anonymous, no viewer org).
  // This mirrors the UI, where the scope dropdown is disabled outside the dashboard placement.
  const onPlacementChange = (placement: BannerPlacement) => {
    if (!state || state.banner.placement === placement) {
      return;
    }
    if (placement !== BannerPlacement.Dashboard) {
      updateBanner({
        placement,
        scope: { type: BannerScope.Global },
      });
      return;
    }
    updateBanner({ placement });
  };

  // Scope toggles are only meaningful when placement is dashboard. The UI gates this by
  // disabling the dropdown elsewhere, but ignore direct calls for safety.
  const onScopeChange = (scope: BannerScope) => {
    if (!state || state.banner.scope?.type === scope) {
      return;
    }
    if (scope === BannerScope.Organizations) {
      if (state.banner.placement !== BannerPlacement.Dashboard) {
        return;
      }
      updateBanner({
        scope: { type: BannerScope.Organizations, organizationIds: [] },
        placement: BannerPlacement.Dashboard,
      });
      return;
    }
    updateBanner({ scope: { type: BannerScope.Global } });
  };

  const onScopedOrganizationsChange = (organizationIds: number[]) => {
    if (!state || state.banner.scope?.type !== BannerScope.Organizations) {
      return;
    }
    updateBanner({
      scope: { type: BannerScope.Organizations, organizationIds },
      placement: BannerPlacement.Dashboard,
    });
  };

  return {
    state,
    isEdited,
    onAddNewBanner,
    onEditBanner,
    onDuplicateBanner,
    onCloseModal,
    onTitleChange,
    onBodyChange,
    onStartDateChange,
    onEndDateChange,
    onToggleIsDismissible,
    onBannerStyleChange,
    onPlacementChange,
    onScopeChange,
    onScopedOrganizationsChange,
  };
};

export default useAddEditBanner;
